import type * as THREE from "three";

/**
 * Wall catalog / Ready textures sample metre-space `uv1` from
 * `applyWallTextureUVs`. `surfaceRef.repeatScale` is tiles-per-metre
 * horizontally (`texture.repeat.x`). Optional `repeatScaleY` sets vertical
 * tiles-per-metre; when omitted, height follows the albedo aspect.
 */

/** Default wallpaper / surface tile width when the user has not set a scale. */
export const DEFAULT_WALL_TEXTURE_TILE_METERS = 4;

export type WallTextureRepeatXY = {
  repeatX: number;
  repeatY: number;
};

/**
 * Convert a physical tile size in metres to Three.js `texture.repeat`
 * for metre-space wall UVs (tiles per metre).
 */
export function wallTextureRepeatScaleFromTileMeters(meters: number): number {
  return 1 / Math.max(meters, 0.01);
}

/**
 * Convert stored tiles-per-metre back to metres-per-tile for the UI.
 * Unset horizontal values resolve to {@link DEFAULT_WALL_TEXTURE_TILE_METERS}.
 */
export function wallTextureTileMetersFromRepeatScale(
  repeatScale: number | undefined | null,
  fallbackMeters: number | null = DEFAULT_WALL_TEXTURE_TILE_METERS,
): number | null {
  if (repeatScale === undefined || repeatScale === null) {
    return fallbackMeters;
  }
  return 1 / Math.max(repeatScale, 0.01);
}

/**
 * Resolve the vertical tile size used for aspect linking. Explicit height
 * wins; otherwise derive metres from horizontal tile size × albedo pixel
 * aspect (same rule as {@link wallTextureRepeatXYFromScaleAndAspect}).
 */
export function wallTextureEffectiveTileHeightMeters(params: {
  widthMeters: number;
  heightMeters: number | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
}): number | null {
  if (params.heightMeters !== null && params.heightMeters > 0) {
    return params.heightMeters;
  }
  const imageWidth = params.imageWidth ?? 0;
  const imageHeight = params.imageHeight ?? 0;
  if (!(params.widthMeters > 0) || !(imageWidth > 0) || !(imageHeight > 0)) {
    return null;
  }
  return params.widthMeters * (imageHeight / imageWidth);
}

/**
 * When wallpaper tile aspect is locked, return the paired width/height so both
 * axes stay proportional. When height is still "auto", pass albedo pixel size
 * so the effective height can be derived from image aspect.
 */
export function linkedWallTileMeters(params: {
  changed: "width" | "height";
  nextMeters: number;
  currentWidthMeters: number;
  currentHeightMeters: number | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
}): { widthMeters: number; heightMeters: number } | null {
  const { changed, nextMeters, currentWidthMeters } = params;
  const effectiveHeightMeters = wallTextureEffectiveTileHeightMeters({
    widthMeters: currentWidthMeters,
    heightMeters: params.currentHeightMeters,
    imageWidth: params.imageWidth,
    imageHeight: params.imageHeight,
  });
  if (
    !(nextMeters > 0) ||
    !(currentWidthMeters > 0) ||
    effectiveHeightMeters === null ||
    !(effectiveHeightMeters > 0)
  ) {
    return null;
  }
  const ratio = currentWidthMeters / effectiveHeightMeters;
  if (changed === "width") {
    return { widthMeters: nextMeters, heightMeters: nextMeters / ratio };
  }
  return { widthMeters: nextMeters * ratio, heightMeters: nextMeters };
}

/**
 * Resolve the effective horizontal tiles-per-metre for a catalog albedo.
 * Metre-space wall maps default to one tile every {@link DEFAULT_WALL_TEXTURE_TILE_METERS}.
 */
export function resolveCatalogTextureRepeatScale(params: {
  repeatScale?: number | null;
  defaultsRepeatScale?: number | null;
  /** Wall faces use metre `uv1`; furniture / floors keep unit UV defaults. */
  metreSpaceUvs?: boolean;
}): number {
  const explicit = params.repeatScale ?? params.defaultsRepeatScale;
  if (explicit !== undefined && explicit !== null) {
    return explicit;
  }
  if (params.metreSpaceUvs) {
    return wallTextureRepeatScaleFromTileMeters(DEFAULT_WALL_TEXTURE_TILE_METERS);
  }
  return 1;
}

/**
 * Resolve optional vertical tiles-per-metre (undefined = derive from aspect).
 */
export function resolveCatalogTextureRepeatScaleY(params: {
  repeatScaleY?: number | null;
  defaultsRepeatScaleY?: number | null;
}): number | undefined {
  const explicit = params.repeatScaleY ?? params.defaultsRepeatScaleY;
  if (explicit === undefined || explicit === null) return undefined;
  return explicit;
}

/**
 * Aspect-correct (or explicitly sized) Three.js repeat for metre-space wall UVs.
 *
 * `repeatScale` is horizontal tiles per metre. When `repeatScaleY` is set it is
 * used for the vertical axis; otherwise height preserves the image aspect.
 */
export function wallTextureRepeatXYFromScaleAndAspect(
  repeatScale: number,
  imageWidth: number,
  imageHeight: number,
  repeatScaleY?: number | null,
): WallTextureRepeatXY {
  const repeatX = Math.max(repeatScale, 1e-6);
  if (repeatScaleY !== undefined && repeatScaleY !== null) {
    return { repeatX, repeatY: Math.max(repeatScaleY, 1e-6) };
  }
  const width = Math.max(imageWidth, 1);
  const height = Math.max(imageHeight, 1);
  return {
    repeatX,
    repeatY: repeatX * (width / height),
  };
}

/**
 * Read pixel size from a loaded Three.js texture image when available.
 */
export function getTextureImageSize(
  texture: THREE.Texture,
): { width: number; height: number } | null {
  const image = texture.image as
    | {
        width?: number;
        height?: number;
        naturalWidth?: number;
        naturalHeight?: number;
      }
    | undefined;
  if (!image) return null;
  const width = image.naturalWidth || image.width || 0;
  const height = image.naturalHeight || image.height || 0;
  if (width <= 0 || height <= 0) return null;
  return { width, height };
}
